import { defineStore } from 'pinia';

/**
 * Defines a Pinia store from a Vuex module left unchanged (transitional, KP-7419).
 *
 * - mutations and actions become Pinia actions under the same names;
 * - a getter keeps its name, or becomes `<name>Getter` when a state key already uses that name
 *   (Pinia forbids the clash);
 * - getters and actions receive what Vuex gave them: `(state, getters)` and
 *   `{ state, getters, commit }`.
 *
 * @param {Object} [options] - Additional Pinia options, e.g. `persist`
 */
export default function defineStoreFromModule(id, module, options = {}) {
  const {
    state = {}, getters = {}, mutations = {}, actions = {},
  } = module;
  // Vuex accepts the state as an object or as a function returning it
  const stateOf = typeof state === 'function' ? state : () => state;
  const stateKeys = Object.keys(stateOf());

  const getterName = (name) => (stateKeys.includes(name) ? `${name}Getter` : name);

  // The `getters` object of Vuex, read on the store
  const gettersOf = (store) => new Proxy({}, { get: (_, name) => store[getterName(name)] });

  return defineStore(id, {
    // The object exported by the module, as Vuex used it
    state: stateOf,
    getters: Object.fromEntries(Object.entries(getters).map(([name, getter]) => [
      getterName(name),
      function vuexGetter(storeState) {
        return getter(storeState, gettersOf(this));
      },
    ])),
    actions: {
      ...Object.fromEntries(Object.entries(mutations).map(([name, mutation]) => [
        name,
        function vuexMutation(payload) {
          mutation(this.$state, payload);
        },
      ])),
      ...Object.fromEntries(Object.entries(actions).map(([name, action]) => [
        name,
        function vuexAction(payload) {
          return action({
            state: this.$state,
            getters: gettersOf(this),
            commit: (type, commitPayload) => this[type](commitPayload),
          }, payload);
        },
      ])),
    },
    ...options,
  });
}
